import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Box,
  useMediaQuery,
  useTheme,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Divider,
  Chip,
  Collapse,
} from "@mui/material";
import { Link, useNavigate, useLocation } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import CategoryIcon from "@mui/icons-material/Category";
import ListIcon from "@mui/icons-material/List";
import SearchIcon from "@mui/icons-material/Search";
import LogoutIcon from "@mui/icons-material/Logout";
// import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownRoundedIcon from "@mui/icons-material/ArrowDropDownRounded";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

// Import your images
import IndiaRiseImg from "../assets/images/india-rise.png";
import JhonsHopkins from "../assets/images/johns-hopkins.png";

// Utility function to generate paths
const generatePath = (name) => {
  const cleanName = name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .trim();
  return `https://indiarisefellowship.in/${cleanName}`;
};

// Check authentication status
const getAuthStatus = () => {
  const user = JSON.parse(localStorage.getItem("user") || "null");
  const isAuthenticated = localStorage.getItem("isAuthenticated");
  return { user, isAuthenticated: user && isAuthenticated };
};

const Header = ({ title }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [submenuAnchor, setSubmenuAnchor] = useState({});
  const [expandedItems, setExpandedItems] = useState(new Set());

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { user, isAuthenticated } = getAuthStatus();
  const isLogin = location.pathname === "/login";

  // Menu items for authenticated users
  const authenticatedMenuItems = [
    {
      text: "Opportunities List",
      icon: <ListIcon />,
      path: "/resources-and-opportunities",
    },
    {
      text: "Resources and Opportunities",
      icon: <SearchIcon />,
      path: "/research",
    },
    { text: "Category", icon: <CategoryIcon />, path: "/category" },
  ];

  // Navigation items for public header
  const publicNavItems = [
    {
      name: "About",
      hasSubmenu: true,
      submenu: [
        "Steering Committee & Core Team",
        "Fellows",
        "Program Mentors",
        "Donors and Partners",
      ],
    },
    {
      name: "Program Overview",
      hasSubmenu: true,
      submenu: ["Program Outline", "RISE Curriculum"],
    },
    {
      name: "Apply",
      hasSubmenu: true,
      submenu: ["Eligibility and Application", "FAQs"],
    },
    {
      name: "Resources and Opportunities",
      hasSubmenu: false,
      link: "/resources-and-opportunities",
    },
    {
      name: "Media Hub",
      hasSubmenu: true,
      submenu: ["RISE Stories", "Publications", "Past and Upcoming Events"],
    },
    {
      name: "Get Involved",
      hasSubmenu: true,
      submenu: ["Join RISE Network", "Partner with Us / Donate", "Contact"],
    },
  ];

  // Handlers
  const handleMenu = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    localStorage.removeItem("isAuthenticated");
    localStorage.removeItem("user");
    localStorage.removeItem("authToken");
    handleClose();
    navigate("/login");
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleSubmenuOpen = (event, menuName) => {
    setSubmenuAnchor((prev) => ({ ...prev, [menuName]: event.currentTarget }));
  };

  const handleSubmenuClose = (menuName) => {
    setSubmenuAnchor((prev) => ({ ...prev, [menuName]: null }));
  };

  const toggleExpandedItem = (itemName) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemName)) {
        newSet.delete(itemName);
      } else {
        newSet.add(itemName);
      }
      return newSet;
    });
  };

  const isActiveRoute = (path) => location.pathname === path;

  // Drawer for authenticated users
  const AuthenticatedDrawer = (
    <Box
      sx={{
        width: 280,
        height: "100%",
        background: "linear-gradient(180deg, #1e3a5f 0%, #2d5a8f 100%)",
        color: "white",
      }}
      role="presentation"
    >
      <Box sx={{ bgcolor: "#fff", cursor: "pointer" }} onClick={() => window.location.href = "https://indiarisefellowship.in"}>
        <img src={IndiaRiseImg} alt="India-Rise" width="100%" />
      </Box>

      {/* Drawer Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Avatar
            sx={{
              bgcolor: "white",
              color: "#1e3a5f",
              width: 40,
              height: 40,
              fontWeight: 600,
            }}
          >
            R
          </Avatar>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
              Research Hub
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Opportunity Portal
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            color: "white",
            "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ px: 2, py: 2 }}>
        {authenticatedMenuItems.map((item) => {
          const isActive = isActiveRoute(item.path);
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  setDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  bgcolor: isActive ? "rgba(255,255,255,0.15)" : "transparent",
                  border: isActive
                    ? "1px solid rgba(255,255,255,0.3)"
                    : "1px solid transparent",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "& .MuiListItemIcon-root": {
                    color: "white",
                    minWidth: 40,
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                    fontSize: "0.95rem",
                  }}
                />
                {isActive && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#4ade80",
                      ml: 1,
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Drawer Footer */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          p: 2,
          borderTop: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            p: 1.5,
            bgcolor: "rgba(255,255,255,0.1)",
            borderRadius: 2,
          }}
        >
          <Avatar
            sx={{ width: 36, height: 36, bgcolor: "white", color: "#1e3a5f" }}
          >
            A
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "0.9rem" }}
            >
              Admin User
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.7, fontSize: "0.75rem" }}
            >
              admin@research.com
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // Drawer for public users
  const PublicDrawer = (
    <Box
      sx={{
        width: "100%",
        maxWidth: 350,
        height: "100%",
        bgcolor: "#0F3A6B", // Deep Blue
        color: "white",
      }}
      role="presentation"
    >
      {/* Drawer Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "flex-end", // Right align close button
        }}
      >
        <IconButton
          onClick={toggleDrawer(false)}
          sx={{
            color: "white",
            bgcolor: "#1976d2", // Lighter blue for the button
            borderRadius: 1, // Square look
            width: 40,
            height: 40,
            "&:hover": {
              bgcolor: "#1565c0",
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 24, fontWeight: "bold" }} />
        </IconButton>
      </Box>

      {/* Navigation Items */}
      <List sx={{ px: 3, pt: 0 }}>
        {publicNavItems.map((item) => (
          <Box key={item.name}>
            <ListItem disablePadding sx={{ mb: 1.5 }}>
              <ListItemButton
                component={!item.hasSubmenu ? Link : "div"}
                to={!item.hasSubmenu ? item.link || generatePath(item.name) : undefined}
                onClick={() => {
                  if (!item.hasSubmenu) {
                    setDrawerOpen(false);
                  } else {
                    toggleExpandedItem(item.name);
                  }
                }}
                sx={{
                  borderRadius: 1,
                  py: 1,
                  px: 0, // Remove padding to align left text
                  color: "white",
                  display: 'flex',
                  justifyContent: 'space-between',
                  "&:hover": {
                    bgcolor: "transparent", // No hover bg, maybe text opacity
                    opacity: 0.9
                  },
                }}
                disableRipple
              >
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: "1.25rem", // Larger font
                    fontWeight: 700,
                    fontFamily: '"Roboto Slab", Serif', // Ensuring font match
                  }}
                />
                {item.hasSubmenu && (
                  expandedItems.has(item.name) ?
                    <RemoveIcon sx={{ color: "white" }} /> :
                    <AddIcon sx={{ color: "white" }} />
                )}
              </ListItemButton>
            </ListItem>
            {item.hasSubmenu && (
              <Collapse in={expandedItems.has(item.name)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding sx={{ pl: 0, pb: 2 }}>
                  {item.submenu.map((subItem) => (
                    <ListItem key={subItem} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        component={Link}
                        to={generatePath(subItem)}
                        onClick={() => setDrawerOpen(false)}
                        sx={{
                          py: 0.5,
                          px: 0,
                          color: "rgba(255,255,255,0.8)",
                          "&:hover": {
                            bgcolor: "transparent",
                            color: "white",
                          },
                        }}
                        disableRipple
                      >
                        <ListItemText
                          primary={subItem}
                          primaryTypographyProps={{
                            fontSize: "1rem",
                            fontWeight: 400,
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </Box>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={1}
        sx={{
          background:
            isLogin || isAuthenticated
              ? "linear-gradient(135deg, #1e3a5f 0%, #2d5a8f 100%)"
              : "white",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Toolbar sx={{ py: 1 }}>
          {isAuthenticated ? (
            <>
              {/* Authenticated User Header */}
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{
                  mr: 2,
                  bgcolor: "rgba(255,255,255,0.1)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.2)",
                    transform: "scale(1.05)",
                  },
                  transition: "all 0.2s ease",
                }}
                onClick={toggleDrawer(true)}
              >
                <MenuIcon />
              </IconButton>

              <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                <Avatar
                  sx={{
                    bgcolor: "white",
                    color: "#1e3a5f",
                    mr: 1.5,
                    width: { xs: 32, sm: 40 },
                    height: { xs: 32, sm: 40 },
                    display: { xs: "none", sm: "flex" },
                    fontWeight: 700,
                  }}
                >
                  R
                </Avatar>
                <Box>
                  <Typography
                    variant={isMobile ? "body1" : "h6"}
                    component="div"
                    sx={{
                      fontWeight: 700,
                      fontSize: { xs: "1rem", sm: "1.25rem" },
                      lineHeight: 1.2,
                      letterSpacing: "0.5px",
                      color: "white",
                    }}
                  >
                    {title}
                  </Typography>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.8,
                        fontSize: "0.75rem",
                        color: "white",
                      }}
                    >
                      Resources & Opportunities Management
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {!isMobile && (
                  <Chip
                    label="Admin"
                    size="small"
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      color: "white",
                      fontWeight: 500,
                      borderRadius: 2,
                    }}
                  />
                )}
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  sx={{
                    color: "white",
                    bgcolor: "rgba(255,255,255,0.1)",
                    "&:hover": {
                      bgcolor: "rgba(255,255,255,0.2)",
                    },
                  }}
                >
                  <AccountCircle />
                </IconButton>
              </Box>
            </>
          ) : (
            <>
              {/* Public/Login Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  px: { xs: 1, sm: 3 },
                }}
              >
                {/* Left Logo */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    bgcolor: isLogin ? "#fff" : "transparent",
                  }}
                >
                  <Box
                    component="img"
                    src={IndiaRiseImg}
                    alt="India Rise"
                    onClick={() => window.location.href = "https://indiarisefellowship.in"}
                    sx={{
                      height: { xs: 35, sm: 45, md: 55 },
                      width: "auto",
                      objectFit: "contain",
                      maxWidth: { xs: "120px", sm: "150px" },
                      cursor: "pointer",
                    }}
                  />
                </Box>

                {/* Center Navigation - Desktop Only */}
                {!isMobile && !isLogin && (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 3,
                      alignItems: "center",
                    }}
                  >
                    {publicNavItems.map((item) => (
                      <Box
                        key={item.name}
                        onMouseEnter={(e) =>
                          item.hasSubmenu && handleSubmenuOpen(e, item.name)
                        }
                        onMouseLeave={() =>
                          item.hasSubmenu && handleSubmenuClose(item.name)
                        }
                        sx={{
                          position: "relative",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 0.5,
                            cursor: "pointer",
                            py: 1,
                          }}
                        >
                          <Typography
                            component={!item.hasSubmenu ? Link : "span"}
                            to={
                              !item.hasSubmenu
                                ? item.link || generatePath(item.name)
                                : undefined
                            }
                            sx={{
                              fontSize: "0.9rem",
                              fontWeight: 600,
                              lineHeight: 1,
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                              pb: 0.5,
                              transition: "all 0.3s ease",
                              textDecoration: "none",
                              color:
                                location.pathname === item.link
                                  ? "#447ebfff"
                                  : "#1e3a5f",
                              "&:hover": {
                                color: "#447ebfff",
                              },
                              "&::after": {
                                content: '""',
                                position: "absolute",
                                bottom: -2,
                                left: 0,
                                width: "100%",
                                height: "3px",
                                transition: "background-color 0.3s ease",
                                bgcolor:
                                  location.pathname === item.link
                                    ? "#FF9E1B"
                                    : "transparent",
                              },
                              "&:hover::after": {
                                bgcolor: "#FF9E1B",
                              },
                            }}
                          >
                            {item.name}
                          </Typography>

                          {item.hasSubmenu && (
                            <ArrowDropDownRoundedIcon
                              fontSize="large"
                              sx={{
                                color: "#FF9E1B",
                                transition: "transform 0.3s ease",
                                transform: submenuAnchor[item.name]
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                verticalAlign: "middle",
                              }}
                            />
                          )}
                        </Box>

                        {/* Submenu Dropdown */}
                        {item.hasSubmenu && (
                          <Menu
                            anchorEl={submenuAnchor[item.name]}
                            open={Boolean(submenuAnchor[item.name])}
                            onClose={() => handleSubmenuClose(item.name)}
                            MenuListProps={{
                              onMouseLeave: () => handleSubmenuClose(item.name),
                            }}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "left",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "left",
                            }}
                            PaperProps={{
                              elevation: 3,
                              sx: {
                                mt: 0.5,
                                backgroundColor: "#497ccf",
                                minWidth: 200,
                                borderRadius: 0,
                                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                              },
                            }}
                          >
                            {item.submenu?.map((subItem) => (
                              <MenuItem
                                key={subItem}
                                component={Link}
                                to={generatePath(subItem)}
                                onClick={() => handleSubmenuClose(item.name)}
                                sx={{
                                  py: 1.5,
                                  px: 2.5,
                                  fontSize: "0.9rem",
                                  color: "#ffffffff",
                                  textDecoration: "none",
                                  "&:hover": {
                                    bgcolor: "rgba(30, 58, 95, 0.05)",
                                    color: "#ffffffff",
                                  },
                                }}
                              >
                                {subItem}
                              </MenuItem>
                            ))}
                          </Menu>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}

                {isLogin && (
                  <Typography
                    sx={{
                      fontSize: { xs: "0.9rem", sm: "1rem", md: "1.5rem" },
                    }}
                    variant="body1"
                    color="#fff"
                  >
                    Resources & Opportunities Management
                  </Typography>
                )}

                {/* Right Logo */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    component="img"
                    src={JhonsHopkins}
                    alt="Johns Hopkins"
                    onClick={() => window.open("https://indiainstitute.jhu.edu/", "_blank")}
                    sx={{
                      height: { xs: 35, sm: 45, md: 55 },
                      width: "auto",
                      objectFit: "contain",
                      maxWidth: { xs: "120px", sm: "180px" },
                      cursor: "pointer",
                    }}
                  />
                </Box>
                {/* Mobile Menu Button */}
                {isMobile && (
                  <IconButton
                    size="large"
                    edge="start"
                    aria-label="menu"
                    sx={{
                      mr: 1,
                      color: "#1e3a5f",
                    }}
                    onClick={toggleDrawer(true)}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
              </Box>
            </>
          )}
        </Toolbar>
      </AppBar>

      {/* User Menu Dropdown */}
      {isAuthenticated && (
        <Menu
          id="menu-appbar"
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          PaperProps={{
            elevation: 3,
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: 2,
              overflow: "visible",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#1e3a5f" }}
            >
              {user?.name || "Admin User"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user?.email || "admin@research.com"}
            </Typography>
          </Box>
          <Divider />
          {/* <MenuItem
            onClick={handleClose}
            sx={{
              py: 1.5,
              "&:hover": {
                bgcolor: "rgba(30, 58, 95, 0.05)",
              },
            }}
          >
            <ListItemIcon>
              <PersonIcon fontSize="small" sx={{ color: "#1e3a5f" }} />
            </ListItemIcon>
            <ListItemText>Profile</ListItemText>
          </MenuItem> */}
          <MenuItem
            onClick={handleLogout}
            sx={{
              py: 1.5,
              color: "error.main",
              "&:hover": {
                bgcolor: "rgba(211, 47, 47, 0.05)",
              },
            }}
          >
            <ListItemIcon>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>Logout</ListItemText>
          </MenuItem>
        </Menu>
      )}

      {/* Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            border: "none",
          },
        }}
      >
        {isAuthenticated ? AuthenticatedDrawer : PublicDrawer}
      </Drawer>
    </>
  );
};

export default Header;
